import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app, db } from "../firebase.config";
import { collection, addDoc, doc, deleteDoc } from "firebase/firestore";
import { getBookingsFromCache } from "../services/bookingsCache";
import { formatPhoneNumber, capitalizeWords } from "../utils/formatting";
import AuthHeader from "../components/AuthHeader";
import { useSelectedDate, useSettings, useProducts, useBookings, useAuthStore } from "../stores";
import { getTenantStoreContext } from "../services/userStoreCache";

const auth = getAuth(app);

const Calendar = () => {
  const navigate = useNavigate();
  const bookings = useBookings((state) => state.bookings);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const scrollViewRef = React.useRef(null);
  // Selected date store
  const getCalendarDate = useSelectedDate((state) => state.getCalendarDate);
  const setCalendarDate = useSelectedDate((state) => state.setCalendarDate);

  const [selectedDate, setSelectedDate] = useState(() => {
    // Always start with today's date on page load
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // Clear selected date on unmount
  useEffect(() => {
    return () => {
      setCalendarDate(null);
    };
  }, [setCalendarDate]);

  // Update Zustand when date changes
  useEffect(() => {
    setCalendarDate(selectedDate);
  }, [selectedDate, setCalendarDate]);
  const [selectedTab, setSelectedTab] = useState("list"); // "delivery", "pickup", or "list"

  // Auto-scroll to bottom when tab changes
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setTimeout(() => {
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollToEnd({ animated: true });
      }
    }, 100);
  };

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [refreshing, setRefreshing] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [shopInfo, setShopInfo] = useState({
    storeName: "",
    address: "",
    phone: "",
    email: "",
  });
  const [storeLogo, setStoreLogo] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auth state - handled by store

  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  
  // Get products from Zustand store for name lookups
  const products1 = useProducts((state) => state.products1);
  const products2 = useProducts((state) => state.products2);
  
  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const storeName = settings.appHeaderTitle?.title || "";
      const address = settings.startingAddress?.address || "";
      const phone = settings.storePhone?.phone || "";
      const email = settings.storeEmail?.email || "";
      const logo = settings.storeLogo?.url || null;

      setShopInfo({
        storeName: storeName,
        address: address,
        phone: phone,
        email: email,
      });
      setStoreLogo(logo);
    }
  }, [settings]);

  const onRefresh = () => {
    setRefreshing(true);
    // Refresh logic - simplified since store updates automatically
    setTimeout(() => {
      setRefreshing(false);
    }, 1000);
  };

  // Helper: Parse Firestore timestamp to milliseconds
  const parseFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.seconds) {
      return timestamp.seconds * 1000;
    }
    return null;
  };

  // Helper: Get TZ day key for comparison (shared function)
  const getTZDayKey = (ms) => {
    try {
      if (ms == null) return null;
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date(ms));
      const y = parts.find((p) => p.type === "year")?.value;
      const m = parts.find((p) => p.type === "month")?.value;
      const d = parts.find((p) => p.type === "day")?.value;
      return y && m && d ? `${y}-${m}-${d}` : null;
    } catch (e) {
      return null;
    }
  };

  // Check if there are bookings to print for selected date (start date must match)
  const hasBookingsToPrint = useMemo(() => {
    if (!selectedDate) return false;
    
    const dateKey = getTZDayKey(selectedDate.getTime());
    return bookings.some((booking) => {
      const startMillis = parseFirestoreTimestamp(booking.startDate);
      if (!startMillis) return false;
      const startKey = getTZDayKey(startMillis);
      return startKey === dateKey;
    });
  }, [selectedDate, bookings]);

  // Get ALL bookings for a specific date (for calendar dots - independent of tab)
  const getAllBookingsForDate = (date) => {
    const dateKey = getTZDayKey(date.getTime());

    return bookings.filter((booking) => {
      // Get start/end millis
      const startMillis = parseFirestoreTimestamp(booking.startDate);
      const endMillis = parseFirestoreTimestamp(booking.endDate);

      if (!startMillis && !endMillis) return false;

      const startKey = getTZDayKey(startMillis);
      const endKey = getTZDayKey(endMillis);

      // Show if start or end date matches (no tab filtering)
      return startKey === dateKey || endKey === dateKey;
    });
  };

  // Get bookings for a specific date (filtered by tab)
  const getBookingsForDate = (date) => {
    const dateKey = getTZDayKey(date.getTime());

    return bookings.filter((booking) => {
      // Get start/end millis
      const startMillis = parseFirestoreTimestamp(booking.startDate);
      const endMillis = parseFirestoreTimestamp(booking.endDate);

      if (!startMillis && !endMillis) return false;

      const startKey = getTZDayKey(startMillis);
      const endKey = getTZDayKey(endMillis);

      // Check if start or end date matches the selected date
      const startMatchesDate = startKey === dateKey;
      const endMatchesDate = endKey === dateKey;

      const hasDelivery = booking.delivery || false;
      const hasHomePickup = booking.homePickup || false;
      const hasInStorePickup = booking.inStorePickup || false;

      // Filter by tab
      if (selectedTab === "delivery") {
        // Delivery tab: show if (start OR end date matches) AND (homePickup OR delivery is selected)
        return (startMatchesDate || endMatchesDate) && (hasDelivery || hasHomePickup);
      } else if (selectedTab === "pickup") {
        // In-store tab: show if start date matches AND (delivery is NOT selected AND homePickup is NOT selected)
        // Only show items going out (not returns)
        return startMatchesDate && !hasDelivery && !hasHomePickup;
      } else {
        // List tab: show all bookings for that day (from both delivery and in-store tabs)
        const isDeliveryTabBooking = (startMatchesDate || endMatchesDate) && (hasDelivery || hasHomePickup);
        const isInStoreTabBooking = startMatchesDate && !hasDelivery && !hasHomePickup;
        return isDeliveryTabBooking || isInStoreTabBooking;
      }
    });
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(null);
  };

  // Print function - writes documents to printing collection
  const handlePrint = async () => {
    if (!selectedDate) {
      alert("Please select a date first");
      return;
    }

    try {
      const { tenantId, storeId } = getTenantStoreContext();
      if (!tenantId || !storeId) {
        alert("Error: Tenant/Store context not available");
        return;
      }

      // Get bookings for selected date with delivery or inStorePickup
      const dateKey = getTZDayKey(selectedDate.getTime());
      const bookingsToPrint = bookings.filter((booking) => {
        const startMillis = parseFirestoreTimestamp(booking.startDate);
        if (!startMillis) return false;
        const startKey = getTZDayKey(startMillis);
        
        // Only bookings where start date matches selected date
        if (startKey !== dateKey) return false;
        
        // Must have either delivery or inStorePickup
        const hasDelivery = booking.delivery || false;
        const hasInStorePickup = booking.inStorePickup || booking.homePickup || false;
        
        return hasDelivery || hasInStorePickup;
      });

      if (bookingsToPrint.length === 0) {
        alert("No bookings with delivery or in-store pickup found for selected date");
        return;
      }

      // Get product category names from settings
      const product1Name = settings.product1Title?.title || "Product1";
      const product2Name = settings.product2Title?.title || "Product2";

      // Format date string to "Mon, Jan 15" format
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const dateString = `${dayNames[selectedDate.getDay()]}, ${monthNames[selectedDate.getMonth()]} ${selectedDate.getDate()}`;

      // Create printing collection path
      const printingPath = `tenants/${tenantId}/stores/${storeId}/printing`;
      const printingRef = collection(db, printingPath);

      const documentIds = [];

      // Process each booking
      for (const booking of bookingsToPrint) {
        // Format product1 array (names only with quantity prefix in parentheses)
        // Exclude items with physicalItem = false
        const product1Array = [];
        if (Array.isArray(booking.product1)) {
          booking.product1.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products1.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              product1Array.push(`(${item.quantity}x) ${productName}`);
            }
          });
        }

        // Format product2 array (names only with quantity prefix in parentheses)
        // Exclude items with physicalItem = false
        const product2Array = [];
        if (Array.isArray(booking.product2)) {
          booking.product2.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products2.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              product2Array.push(`(${item.quantity}x) ${productName}`);
            }
          });
        }

        // Format phone number to (xxx) xxx-xxxx
        const rawPhone = booking.customerPhone || booking.phone || "";
        const formattedPhone = formatPhoneNumber(rawPhone);

        // Create document data
        const documentData = {
          firstName: capitalizeWords(booking.firstName || ""),
          lastName: capitalizeWords(booking.lastName || ""),
          phoneNumber: formattedPhone,
          dateString: dateString,
          product1: product1Array,
          product2: product2Array,
          product1Name: product1Name,
          product2Name: product2Name,
          delivery: booking.delivery || false,
          inStorePickup: booking.homePickup || booking.inStorePickup || false,
          adminNotes: booking.adminNotes || "",
        };

        // Add document to collection
        const docRef = await addDoc(printingRef, documentData);
        documentIds.push(docRef.id);
        console.log(`✅ Created print document ${docRef.id} for booking ${booking.id || booking.bookingId}`);
      }

      // Schedule cleanup after 15 seconds
      setTimeout(() => {
        cleanupPrintDocuments(printingPath, documentIds);
      }, 15000);
    } catch (error) {
      console.error("❌ Error printing:", error);
      alert(`Failed to print: ${error.message}`);
    }
  };

  // Print All function - prints bookings where start date matches selected date
  const handleTestPrint = async () => {
    if (!selectedDate) {
      alert("Please select a date first");
      return;
    }

    try {
      const { tenantId, storeId } = getTenantStoreContext();
      if (!tenantId || !storeId) {
        alert("Error: Tenant/Store context not available");
        return;
      }

      // Get bookings where start date matches selected date
      const dateKey = getTZDayKey(selectedDate.getTime());
      const bookingsToPrint = bookings.filter((booking) => {
        const startMillis = parseFirestoreTimestamp(booking.startDate);
        
        if (!startMillis) return false;
        
        const startKey = getTZDayKey(startMillis);
        
        // Only print if start date matches selected date
        return startKey === dateKey;
      });

      if (bookingsToPrint.length === 0) {
        alert("No bookings found for selected date");
        return;
      }

      // Get product category names from settings
      const product1Name = settings.product1Title?.title || "Product1";
      const product2Name = settings.product2Title?.title || "Product2";

      // Format date string to "Mon, Jan 15" format
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const dateString = `${dayNames[selectedDate.getDay()]}, ${monthNames[selectedDate.getMonth()]} ${selectedDate.getDate()}`;

      // Create printing collection path
      const printingPath = `tenants/${tenantId}/stores/${storeId}/printing`;
      const printingRef = collection(db, printingPath);

      const documentIds = [];

      // Process each booking
      for (const booking of bookingsToPrint) {
        // Format product1 array (names only with quantity prefix in parentheses)
        // Exclude items with physicalItem = false
        const product1Array = [];
        if (Array.isArray(booking.product1)) {
          booking.product1.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products1.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              const formattedProduct = `(${item.quantity}x) ${productName}`;
              product1Array.push(formattedProduct);
            }
          });
        }

        // Format product2 array (names only with quantity prefix in parentheses)
        // Exclude items with physicalItem = false
        const product2Array = [];
        if (Array.isArray(booking.product2)) {
          booking.product2.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products2.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              const formattedProduct = `(${item.quantity}x) ${productName}`;
              product2Array.push(formattedProduct);
            }
          });
        }

        // Format phone number to (xxx) xxx-xxxx
        const rawPhone = booking.customerPhone || booking.phone || "";
        const formattedPhone = formatPhoneNumber(rawPhone);

        // Create document data
        const documentData = {
          firstName: capitalizeWords(booking.firstName || ""),
          lastName: capitalizeWords(booking.lastName || ""),
          phoneNumber: formattedPhone,
          dateString: dateString,
          product1: product1Array,
          product2: product2Array,
          product1Name: product1Name,
          product2Name: product2Name,
          delivery: booking.delivery || false,
          inStorePickup: booking.homePickup || booking.inStorePickup || false,
          adminNotes: booking.adminNotes || "",
        };

        // Add document to collection
        const docRef = await addDoc(printingRef, documentData);
        documentIds.push(docRef.id);
        console.log(`✅ Created test print document ${docRef.id} for booking ${booking.id || booking.bookingId}`);
      }

      // Schedule cleanup after 15 seconds
      setTimeout(() => {
        cleanupPrintDocuments(printingPath, documentIds);
      }, 15000);
    } catch (error) {
      console.error("❌ Error test printing:", error);
      alert(`Failed to test print: ${error.message}`);
    }
  };

  // Cleanup function - deletes print documents after 15 seconds
  const cleanupPrintDocuments = async (printingPath, documentIds) => {
    try {
      for (const docId of documentIds) {
        try {
          const docRef = doc(db, printingPath, docId);
          await deleteDoc(docRef);
          console.log(`✅ Cleaned up print document ${docId}`);
        } catch (error) {
          // Document may already be deleted by server - this is expected
          if (error.code === 'not-found') {
            console.log(`ℹ️ Document ${docId} already deleted`);
          } else {
            console.error(`⚠️ Error deleting document ${docId}:`, error);
          }
        }
      }
    } catch (error) {
      console.error("❌ Error in cleanup:", error);
    }
  };

  // Helper function to format date as "Mon, Jan 1"
  const formatDateShort = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${dayNames[d.getDay()]}, ${monthNames[d.getMonth()]} ${d.getDate()}`;
  };

  // Helper function to determine card type and styling for a booking
  const getBookingCardInfo = (booking, selectedDate) => {
    const dateKey = getTZDayKey(selectedDate.getTime());
    const startMillis = parseFirestoreTimestamp(booking.startDate);
    const endMillis = parseFirestoreTimestamp(booking.endDate);
    const startKey = getTZDayKey(startMillis);
    const endKey = getTZDayKey(endMillis);

    const hasDelivery = booking.delivery || false;
    const hasHomePickup = booking.homePickup || false;
    const isInStore = booking.inStorePickup || (!hasDelivery && !hasHomePickup);

    const startMatchesDate = startKey === dateKey;
    const endMatchesDate = endKey === dateKey;

    // 1. Delivery (Red) - Priority if start date matches
    if (hasDelivery && startMatchesDate) {
      return { type: "DELIVERY", color: "#C4453D", borderColor: "#C4453D" }; // Red
    }

    // 2. Home Pickup (Green) - Priority if end date matches
    if (hasHomePickup && endMatchesDate) {
      return { type: "PICKUP", color: "#1F8A5B", borderColor: "#1F8A5B" }; // Green
    }

    // 3. In-Store Pickup (Yellow) - Start Date
    if (isInStore && startMatchesDate) {
      return { type: "IN-STORE PICKUP", color: "#B4761B", borderColor: "#B4761B" }; // Yellow
    }

    // 4. In-Store Drop-off (Gray) - End Date
    if (isInStore && endMatchesDate) {
      return { type: "DROP-OFF", color: "#8A9794", borderColor: "#8A9794" }; // Gray
    }

    // Default fallback
    return { type: "RENTAL", color: "#8A9794", borderColor: "#8A9794" };
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get bookings for selected date - show all bookings if list tab, otherwise filter by tab
  const selectedDateBookings = selectedDate
    ? (selectedTab === "list" 
        ? bookings.filter((booking) => {
            const startMillis = parseFirestoreTimestamp(booking.startDate);
            const endMillis = parseFirestoreTimestamp(booking.endDate);
            if (!startMillis && !endMillis) return false;
            const dateKey = getTZDayKey(selectedDate.getTime());
            const startKey = getTZDayKey(startMillis);
            const endKey = getTZDayKey(endMillis);
            return startKey === dateKey || endKey === dateKey;
          })
        : getBookingsForDate(selectedDate))
    : [];

  const currentStyles = isDesktop ? stylesDesktop : stylesMobile;

  if (loading) {
    return (
      <View style={currentStyles.container}>
        <View style={currentStyles.loadingContainer}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={currentStyles.loadingText}>Loading calendar...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={currentStyles.container}>
      <AuthHeader
        title="Calendar"
        subtitle={shopInfo.storeName ? `by ${shopInfo.storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      <View style={isDesktop ? currentStyles.desktopWrapper : null}>
      <ScrollView
          ref={scrollViewRef}
          style={currentStyles.scrollView}
        showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
        {/* Delivery Planner and Test Print Buttons - At Top */}
        <View style={currentStyles.topButtonsContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              currentStyles.deliveryPlannerButton,
              !selectedDate && currentStyles.deliveryPlannerButtonDisabled,
            ]}
            onPress={() => {
              if (selectedDate) {
                navigate("/today", { state: { selectedDate: selectedDate } });
              }
            }}
            disabled={!selectedDate}
          >
            <Text style={currentStyles.deliveryPlannerButtonText}>
              🚚 Today
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              currentStyles.testPrintButton,
              (!selectedDate || !hasBookingsToPrint) && currentStyles.testPrintButtonDisabled,
            ]}
            onPress={handleTestPrint}
            disabled={!selectedDate || !hasBookingsToPrint}
          >
            <Text style={currentStyles.testPrintButtonText}>
              🖨️ Print All
            </Text>
          </TouchableOpacity>
        </View>
        {/* Day Labels - Always Show */}
        <View style={currentStyles.weekLabels}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <Text key={day} style={currentStyles.weekLabel}>
              {day}
            </Text>
          ))}
        </View>
        {/* Calendar Grid - Always Show */}
        <View style={currentStyles.calendarGrid}>
          {calendarDays.map((date, index) => {
            if (!date) {
              return <View key={`empty-${index}`} style={currentStyles.dayCell} />;
            }

            const bookingsOnDate = getAllBookingsForDate(date);
            const isToday = date.getTime() === today.getTime();
            const isSelected =
              selectedDate && getTZDayKey(date.getTime()) === getTZDayKey(selectedDate.getTime());

            // Calculate products going OUT for this date (start dates only, in-store or delivery)
            const dateKey = getTZDayKey(date.getTime());
            const isDateTodayOrAfter = date.getTime() >= today.getTime();
            let productTally = {}; // { productName: quantity }
            let hasDeliveriesOrPickups = false;
            let deliveryCount = 0;
            let pickupCount = 0;

            if (isDateTodayOrAfter) {
            bookingsOnDate.forEach((booking) => {
              const startMillis = parseFirestoreTimestamp(booking.startDate);
              const endMillis = parseFirestoreTimestamp(booking.endDate);
              const startKey = getTZDayKey(startMillis);
              const endKey = getTZDayKey(endMillis);

              // Check if this booking has a delivery or pickup on this date
              if (startKey === dateKey || endKey === dateKey) {
                hasDeliveriesOrPickups = true;
              }

                // Count deliveries (start date matches and has delivery)
                const hasDelivery = booking.delivery || false;
                const hasHomePickup = booking.homePickup || false;
                const isInStorePickup = !hasDelivery && !hasHomePickup;
                
                if (startKey === dateKey && hasDelivery) {
                  deliveryCount++;
                }
                
                // Count pickups (end date matches and has home pickup)
                if (endKey === dateKey && hasHomePickup) {
                  pickupCount++;
                }
                
                // Only count products going OUT if this is a start date and (delivery OR in-store pickup)
              const product1Items = booking.product1;
                if (startKey === dateKey && product1Items && (hasDelivery || isInStorePickup)) {
                  product1Items.forEach((item) => {
                    const productName = item.name || "Unknown";
                    const quantity = item.quantity || 0;
                    productTally[productName] = (productTally[productName] || 0) + quantity;
                });
              }
            });
            } else {
              // For past dates, just check if there are deliveries/pickups for border styling
              bookingsOnDate.forEach((booking) => {
                const startMillis = parseFirestoreTimestamp(booking.startDate);
                const endMillis = parseFirestoreTimestamp(booking.endDate);
                const startKey = getTZDayKey(startMillis);
                const endKey = getTZDayKey(endMillis);
                if (startKey === dateKey || endKey === dateKey) {
                  hasDeliveriesOrPickups = true;
                }
              });
            }

            const hasProducts = Object.keys(productTally).length > 0;
            // Calculate total count of product1 items going out
            const totalProductCount = Object.values(productTally).reduce((sum, qty) => sum + qty, 0);

            // Check for check-in status indicators
            // Green: booking end date matches this day AND end date is yesterday or earlier AND underReview is true
            // Red: booking end date matches this day AND end date is yesterday or earlier AND (no bookingIntake OR underReview is false/missing)
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            
            let hasGreenIndicator = false;
            let hasRedIndicator = false;
            
            // Only check for indicators on dates that are yesterday or earlier
            if (date.getTime() <= yesterday.getTime()) {
              bookingsOnDate.forEach((booking) => {
                const endMillis = parseFirestoreTimestamp(booking.endDate);
                if (!endMillis) return;
                
                const endDate = new Date(endMillis);
                endDate.setHours(0, 0, 0, 0);
                const endKey = getTZDayKey(endMillis);
                
                // Check if booking end date matches this calendar day AND is yesterday or earlier
                if (endKey === dateKey && endDate.getTime() <= yesterday.getTime()) {
                  const bookingIntake = booking.bookingIntake;
                  
                  if (bookingIntake && bookingIntake.underReview === true) {
                    // Green indicator: check-in exists and underReview is true
                    hasGreenIndicator = true;
                  } else if (!bookingIntake || bookingIntake.underReview !== true) {
                    // Red indicator: no bookingIntake OR underReview is false/missing
                    hasRedIndicator = true;
                  }
                }
              });
            }

            return (
              <TouchableOpacity
                key={index}
                style={[
                  currentStyles.dayCell,
                  isToday && !isSelected && currentStyles.todayCell,
                  isSelected && currentStyles.selectedCell,
                  hasDeliveriesOrPickups && currentStyles.deliveryDayCell,
                ]}
                onPress={() => setSelectedDate(date)}
              >
                <Text
                  style={[
                    currentStyles.dayText,
                    isToday && currentStyles.todayText,
                    isSelected && !isToday && currentStyles.selectedText,
                  ]}
                >
                  {date.getDate()}
                </Text>
                {/* Check-in Status Indicators */}
                {hasGreenIndicator && (
                  <View style={currentStyles.checkInIndicatorGreen} />
                )}
                {hasRedIndicator && !hasGreenIndicator && (
                  <View style={currentStyles.checkInIndicatorRed} />
                )}
                {isDesktop && isDateTodayOrAfter && (deliveryCount > 0 || pickupCount > 0) && (
                  <View style={currentStyles.deliveryPickupCounts}>
                    {deliveryCount > 0 && (
                      <Text style={currentStyles.deliveryCountText}>
                        🚚 {deliveryCount}
                      </Text>
                    )}
                    {pickupCount > 0 && (
                      <Text style={currentStyles.pickupCountText}>
                        📦 {pickupCount}
                      </Text>
                    )}
                  </View>
                )}
                {hasProducts && totalProductCount > 0 && (
                  <>
                    {!isDesktop ? (
                      // Mobile: Show count only
                      <Text style={currentStyles.productCount}>
                        {totalProductCount}
                      </Text>
                    ) : (
                      // Desktop: Show product names and quantities
                      <View style={currentStyles.productsList}>
                        {Object.entries(productTally)
                          .sort((a, b) => b[1] - a[1]) // Sort by quantity descending
                          .map(([productName, quantity], idx) => (
                            <View key={idx} style={currentStyles.productItemContainer}>
                              <View style={currentStyles.productBullet} />
                              <Text style={currentStyles.productItem}>
                                {quantity}x {productName}
                              </Text>
                            </View>
                          ))}
                      </View>
                    )}
                  </>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
        {/* Month Navigation - Moved Below Calendar */}
        <View style={currentStyles.monthNavigation}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            onPress={goToPreviousMonth}
            style={currentStyles.monthButton}
          >
            <Text style={currentStyles.monthButtonText}>←</Text>
          </TouchableOpacity>
          <Text style={currentStyles.monthTitle}>
            {currentMonth.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} onPress={goToNextMonth} style={currentStyles.monthButton}>
            <Text style={currentStyles.monthButtonText}>→</Text>
          </TouchableOpacity>
        </View>
        {/* Tab Selector - Always Show */}
        <View style={currentStyles.tabContainer}>
          <TouchableOpacity
            style={[currentStyles.tab, selectedTab === "delivery" && currentStyles.tabActive]}
            onPress={() => handleTabChange("delivery")}
          >
            <Text style={currentStyles.tabIcon}>🚚</Text>
            <Text
              style={[
                currentStyles.tabText,
                selectedTab === "delivery" && currentStyles.tabTextActive,
              ]}
            >
              Deliveries
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[currentStyles.tab, selectedTab === "pickup" && currentStyles.tabActive]}
            onPress={() => handleTabChange("pickup")}
          >
            <Text style={currentStyles.tabIcon}>📦</Text>
            <Text
              style={[
                currentStyles.tabText,
                selectedTab === "pickup" && currentStyles.tabTextActive,
              ]}
            >
              In-Store
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[currentStyles.tab, selectedTab === "list" && currentStyles.tabActive]}
            onPress={() => handleTabChange("list")}
          >
            <Text style={currentStyles.tabIcon}>📋</Text>
            <Text
              style={[
                currentStyles.tabText,
                selectedTab === "list" && currentStyles.tabTextActive,
              ]}
            >
              List
            </Text>
          </TouchableOpacity>
          {/* Print Button - Right Side */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              currentStyles.printButton,
              !selectedDate && currentStyles.printButtonDisabled,
            ]}
            onPress={handlePrint}
            disabled={!selectedDate}
          >
            <Text style={currentStyles.printButtonIcon}>🖨️</Text>
          </TouchableOpacity>
        </View>
        {/* Content below calendar - changes based on tab */}
        {selectedTab === "list" ? (
          <View style={currentStyles.listSection}>
            {(() => {
              // If a date is selected, show bookings for that date only
              // Otherwise show bookings for the entire month
              let filteredBookings;

              if (selectedDate) {
                // Show bookings for selected date - show all bookings (same as selectedDateBookings)
                filteredBookings = bookings.filter((booking) => {
                  const startMillis = parseFirestoreTimestamp(booking.startDate);
                  const endMillis = parseFirestoreTimestamp(booking.endDate);
                  if (!startMillis && !endMillis) return false;
                  const dateKey = getTZDayKey(selectedDate.getTime());
                  const startKey = getTZDayKey(startMillis);
                  const endKey = getTZDayKey(endMillis);
                  return startKey === dateKey || endKey === dateKey;
                });
              } else {
                // Show bookings for entire month
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);

                filteredBookings = bookings.filter((booking) => {
                  const startMillis = parseFirestoreTimestamp(booking.startDate);
                  const endMillis = parseFirestoreTimestamp(booking.endDate);
                  
                  if (!startMillis || !endMillis) return false;
                  
                  const startDate = new Date(startMillis);
                  const endDate = new Date(endMillis);

                  // Check if booking overlaps with the month
                  return startDate <= lastDay && endDate >= firstDay;
                });
              }

              if (filteredBookings.length === 0) {
                return (
                  <Text style={currentStyles.noBookingsText}>
                    {selectedDate
                      ? "No bookings on this date"
                      : "No bookings for this month"}
                  </Text>
                );
              }

              // If a date is selected, show cards (same as delivery/pickup tabs)
              if (selectedDate) {
                return (
                  <View style={currentStyles.bookingsList}>
                    {filteredBookings.map((booking) => {
                      const cardInfo = getBookingCardInfo(booking, selectedDate);
                      const customerName = `${booking.firstName || ""} ${booking.lastName || ""}`.trim() || "Unknown";
                      const capitalizedName = capitalizeWords(customerName);
                      const formattedPhone = formatPhoneNumber(booking.customerPhone || booking.phone || "");
                      
                      // Format duration (start date - end date)
                      const startMillis = parseFirestoreTimestamp(booking.startDate);
                      const endMillis = parseFirestoreTimestamp(booking.endDate);
                      const durationText = startMillis && endMillis 
                        ? `${formatDateShort(startMillis)} - ${formatDateShort(endMillis)}`
                        : "";
                      
                      // Get products rented
                      const product1Items = booking.product1 || [];
                      const product2Items = booking.product2 || [];
                      
                      // Get miles for delivery or pickup
                      const hasDelivery = booking.delivery || false;
                      const hasHomePickup = booking.homePickup || false;
                      const showMiles = hasDelivery || hasHomePickup;
                      const numMiles = booking.numMiles !== undefined && booking.numMiles !== null 
                        ? parseFloat(Number(booking.numMiles).toFixed(1)) 
                        : 0.0;
                      const milesText = showMiles ? `${numMiles} mi` : null;

                      return (
                        <TouchableOpacity
                          key={booking.id}
                          style={[
                            currentStyles.rentalCard,
                            { borderColor: cardInfo.borderColor, borderWidth: 2 }
                          ]}
                          onPress={() => navigate(`/view-booking/${booking.id || booking.bookingId}`)}
                        >
                          <View
                            style={[
                              currentStyles.rentalBadge,
                              { backgroundColor: cardInfo.color }
                            ]}
                          >
                            <Text style={currentStyles.rentalBadgeText}>
                              {cardInfo.type}
                            </Text>
                          </View>
                          <View style={currentStyles.rentalInfo}>
                            {/* Name and Phone on same line */}
                            <View style={currentStyles.namePhoneRow}>
                              <Text style={currentStyles.customerName}>
                                {capitalizedName}
                              </Text>
                              {formattedPhone && (
                                <Text style={currentStyles.rentalDetails}>
                                  {formattedPhone}
                                </Text>
                              )}
                            </View>
                            {/* Duration on left, Miles on right */}
                            <View style={currentStyles.durationMilesRow}>
                              {durationText && (
                                <Text style={currentStyles.rentalDetails}>
                                  {durationText}
                                </Text>
                              )}
                              {milesText && (
                                <Text style={currentStyles.rentalDetails}>
                                  {milesText}
                                </Text>
                              )}
                            </View>
                            {/* Product items container - Centered */}
                            <View style={currentStyles.productsContainer}>
                              {/* Product1 items - Blue */}
                              {product1Items.map((item, idx) => (
                                <Text key={`p1-${idx}`} style={[currentStyles.rentalDetails, currentStyles.product1Item]}>
                                  {item.quantity || 0}x {item.name || "Unknown"}
                                </Text>
                              ))}
                              {/* Product2 items - Green */}
                              {product2Items.map((item, idx) => (
                                <Text key={`p2-${idx}`} style={[currentStyles.rentalDetails, currentStyles.product2Item]}>
                                  {item.quantity || 0}x {item.name || "Unknown"}
                                </Text>
                              ))}
                            </View>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                );
              }

              // If no date selected, show totals summary for the month
              const bicycleTotals = {};
              filteredBookings.forEach((booking) => {
                const product1Items = booking.product1;
                if (product1Items) {
                  product1Items.forEach((bike) => {
                    const name = bike.name || "Unknown";
                    const qty = bike.quantity || 0;
                    bicycleTotals[name] = (bicycleTotals[name] || 0) + qty;
                  });
                }
              });

              return (
                <>
                  {/* Bicycle Totals Summary */}
                  {Object.keys(bicycleTotals).length > 0 && (
                    <View style={currentStyles.totalsSummary}>
                      <Text style={currentStyles.totalsSummaryTitle}>
                        📊 Total Bicycles
                      </Text>
                      {Object.entries(bicycleTotals)
                        .sort((a, b) => b[1] - a[1]) // Sort by quantity descending
                        .map(([name, total]) => (
                          <Text key={name} style={currentStyles.totalItem}>
                            ({total}) {name}
                          </Text>
                        ))}
                    </View>
                  )}

                  {/* Individual Bookings */}
                  {filteredBookings.map((booking) => {
                    const customerName = `${booking.firstName || ""} ${booking.lastName || ""}`.trim() || "Unknown";
                    const product1Items = booking.product1 || [];

                    return (
                      <View key={booking.id} style={currentStyles.listItem}>
                        <Text style={currentStyles.listCustomerName}>
                          {customerName}
                        </Text>
                        {product1Items.map((item, idx) => (
                          <Text key={idx} style={currentStyles.listEquipment}>
                            ({item.quantity || 0}) {item.name || "Unknown"}
                          </Text>
                        ))}
                      </View>
                    );
                  })}
                </>
              );
            })()}
          </View>
        ) : (
          // Delivery/Pickup tabs show selected date details
          selectedDate && (
            <View style={currentStyles.selectedDateSection}>
              <Text style={currentStyles.selectedDateTitle}>
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Text>
              {selectedDateBookings.length === 0 ? (
                <Text style={currentStyles.noBookingsText}>
                  No bookings on this date
                </Text>
              ) : (
                <View style={currentStyles.bookingsList}>
                  {selectedDateBookings.map((booking) => {
                    const cardInfo = getBookingCardInfo(booking, selectedDate);
                    const customerName = `${booking.firstName || ""} ${booking.lastName || ""}`.trim() || "Unknown";
                    const capitalizedName = capitalizeWords(customerName);
                    const formattedPhone = formatPhoneNumber(booking.customerPhone || booking.phone || "");
                    
                    // Format duration (start date - end date)
                    const startMillis = parseFirestoreTimestamp(booking.startDate);
                    const endMillis = parseFirestoreTimestamp(booking.endDate);
                    const durationText = startMillis && endMillis 
                      ? `${formatDateShort(startMillis)} - ${formatDateShort(endMillis)}`
                      : "";

                    // Get miles for delivery or pickup
                    const hasDelivery = booking.delivery || false;
                    const hasHomePickup = booking.homePickup || false;
                    const showMiles = hasDelivery || hasHomePickup;
                    const numMiles = booking.numMiles !== undefined && booking.numMiles !== null 
                      ? parseFloat(Number(booking.numMiles).toFixed(1)) 
                      : 0.0;
                    const milesText = showMiles ? `${numMiles} mi` : null;
                    
                    // Get products rented
                    const product1Items = booking.product1 || [];
                    const product2Items = booking.product2 || [];

                    return (
                        <TouchableOpacity
                          key={booking.id}
                          style={[
                            currentStyles.rentalCard,
                            { borderColor: cardInfo.borderColor, borderWidth: 2 }
                          ]}
                          onPress={() => navigate(`/view-booking/${booking.id || booking.bookingId}`)}
                        >
                        <View
                          style={[
                              currentStyles.rentalBadge,
                              { backgroundColor: cardInfo.color }
                          ]}
                        >
                            <Text style={currentStyles.rentalBadgeText}>
                              {cardInfo.type}
                          </Text>
                        </View>
                          <View style={currentStyles.rentalInfo}>
                            {/* Name and Phone on same line */}
                            <View style={currentStyles.namePhoneRow}>
                              <Text style={currentStyles.customerName}>
                                {capitalizedName}
                          </Text>
                              {formattedPhone && (
                                <Text style={currentStyles.rentalDetails}>
                                  {formattedPhone}
                          </Text>
                              )}
                            </View>
                            {/* Duration on left, Miles on right */}
                            <View style={currentStyles.durationMilesRow}>
                              {durationText && (
                                <Text style={currentStyles.rentalDetails}>
                                  {durationText}
                                </Text>
                              )}
                              {milesText && (
                                <Text style={currentStyles.rentalDetails}>
                                  {milesText}
                            </Text>
                          )}
                        </View>
                          {/* Product items container - Centered */}
                          <View style={currentStyles.productsContainer}>
                            {/* Product1 items - Blue */}
                            {product1Items.map((item, idx) => (
                              <Text key={`p1-${idx}`} style={[currentStyles.rentalDetails, currentStyles.product1Item]}>
                                {item.quantity || 0}x {item.name || "Unknown"}
                              </Text>
                            ))}
                            {/* Product2 items - Green */}
                            {product2Items.map((item, idx) => (
                              <Text key={`p2-${idx}`} style={[currentStyles.rentalDetails, currentStyles.product2Item]}>
                                {item.quantity || 0}x {item.name || "Unknown"}
                              </Text>
                            ))}
                      </View>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          )
        )}
      </ScrollView>
      </View>
    </View>
  );
};

// Mobile Styles
const stylesMobile = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },
  desktopWrapper: {
    // Not used on mobile
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  backButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  headerSpacer: {
    width: 44,
  },
  todayButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#0E7C6B",
    borderRadius: 5,
  },
  todayButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: "#5C6B68",
  },
  scrollView: {
    flex: 1,
  },
  monthNavigation: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
    marginHorizontal: 12,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  monthButton: {
    padding: 8,
  },
  monthButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  weekLabels: {
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
  },
  weekLabel: {
    flex: 1,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    backgroundColor: "#FFFFFF",
    padding: 8,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  dayCell: {
    width: "14.28%", // 100% / 7 days
    aspectRatio: 1,
    padding: 4,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    borderWidth: 0,
    position: "relative",
  },
  todayCell: {
    borderWidth: 2,
    borderColor: "#0E7C6B",
  },
  selectedCell: {
    borderWidth: 2,
    borderColor: "#C4453D", // Red border
  },
  hasBookingsCell: {
    backgroundColor: "#FDF3E3",
  },
  deliveryDayCell: {
    // No border - borders only on today
  },
  dayText: {
    fontSize: 14, // Reduced by 2 from 16
    color: "#2E3A38",
    fontWeight: "500",
  },
  todayText: {
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  selectedText: {
    color: "#2E3A38", // Dark color so it's visible
    fontWeight: "bold",
  },
  rentalDot: {
    marginTop: 2,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#1F8A5B",
    alignItems: "center",
    justifyContent: "center",
  },
  bookingCount: {
    fontSize: 11,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  productCount: {
    marginTop: 2,
    fontSize: 14,
    color: "#C4453D", // Red color
    fontWeight: "bold",
    textAlign: "center",
    width: "100%",
  },
  productsList: {
    marginTop: 2,
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  productItemContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 1,
    width: "100%",
  },
  productBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#0E7C6B",
    marginRight: 4,
    flexShrink: 0,
  },
  productItem: {
    fontSize: 13, // Increased by 1 more from 12
    color: "#2E3A38",
    fontWeight: "500",
    lineHeight: 15, // Increased proportionally
    flexShrink: 1, // Allow text to shrink and wrap
  },
  selectedDateSection: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 12,
    marginTop: 16,
    marginBottom: 20,
    borderRadius: 8,
    padding: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  selectedDateTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
  },
  noBookingsText: {
    fontSize: 16,
    color: "#8A9794",
    textAlign: "center",
    paddingVertical: 20,
  },
  bookingsList: {
    gap: 12,
  },
  bookingCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  rentalCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 12,
  },
  rentalBadge: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    alignItems: "center",
  },
  deliveryBadge: {
    backgroundColor: "#1F8A5B",
  },
  pickupBadge: {
    backgroundColor: "#B4761B",
  },
  rentalBadgeText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  rentalInfo: {
    padding: 12,
  },
  namePhoneRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  durationMilesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  customerName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  rentalDetails: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 4,
  },
  product1Item: {
    color: "#0E7C6B", // Blue
  },
  product2Item: {
    color: "#1F8A5B", // Green
  },
  productsContainer: {
    alignItems: "center",
    marginTop: 4,
  },
  deliveryPickupCounts: {
    flexDirection: "row",
    gap: 6,
    marginTop: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  deliveryCountText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  pickupCountText: {
    fontSize: 14,
    color: "#1F8A5B",
    fontWeight: "bold",
  },
  checkInIndicatorGreen: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#1F8A5B",
    borderWidth: 1,
    borderColor: "#FFFFFF",
  },
  checkInIndicatorRed: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#C4453D",
    borderWidth: 1,
    borderColor: "#FFFFFF",
  },
  topButtonsContainer: {
    flexDirection: "row",
    marginTop: 12,
    marginBottom: 12,
    marginHorizontal: 12,
    gap: 12,
  },
  deliveryPlannerButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    opacity: 1,
  },
  deliveryPlannerButtonDisabled: {
    opacity: 0.5,
    backgroundColor: "#8A9794",
  },
  deliveryPlannerButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  testPrintButton: {
    flex: 1,
    backgroundColor: "#1F8A5B",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    opacity: 1,
  },
  testPrintButtonDisabled: {
    opacity: 0.5,
    backgroundColor: "#8A9794",
  },
  testPrintButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  tabContainer: {
    flexDirection: "row",
    backgroundColor: "#B4761B",
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginTop: 7,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    alignItems: "center",
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 5,
    borderRadius: 6,
    marginHorizontal: 4,
  },
  tabActive: {
    backgroundColor: "#E3F2EF",
  },
  tabIcon: {
    fontSize: 16,
    marginRight: 6,
  },
  printButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "#0E7C6B",
    marginLeft: 8,
  },
  printButtonDisabled: {
    backgroundColor: "#AEB8B6",
    opacity: 0.5,
  },
  printButtonIcon: {
    fontSize: 20,
    color: "#FFFFFF",
  },
  tabText: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "500",
  },
  tabTextActive: {
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  listViewContainer: {
    flex: 1,
  },
  listSection: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 12,
    marginTop: 8,
    marginBottom: 20,
    borderRadius: 8,
    padding: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  listItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  listCustomerName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 6,
  },
  listEquipment: {
    fontSize: 14,
    color: "#5C6B68",
    marginLeft: 8,
    marginBottom: 2,
  },
  totalsSummary: {
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E3F2EF",
  },
  totalsSummaryTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 8,
  },
  totalItem: {
    fontSize: 14,
    color: "#2E3A38",
    marginBottom: 4,
    marginLeft: 8,
  },
});

// Desktop Styles
const stylesDesktop = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  backButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  headerSpacer: {
    width: 44,
  },
  todayButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#0E7C6B",
    borderRadius: 5,
  },
  todayButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: "#5C6B68",
  },
  scrollView: {
    flex: 1,
  },
  monthNavigation: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
    marginHorizontal: 12,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  monthButton: {
    padding: 6,
  },
  monthButtonText: {
    fontSize: 20,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  monthTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  weekLabels: {
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    paddingVertical: 10,
    paddingHorizontal: 8,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
  },
  weekLabel: {
    flex: 1,
    textAlign: "center",
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    backgroundColor: "#FFFFFF",
    padding: 6,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  dayCell: {
    width: "14.28%", // 100% / 7 days
    aspectRatio: 1,
    padding: 2,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    borderWidth: 0,
  },
  todayCell: {
    borderWidth: 2,
    borderColor: "#0E7C6B",
  },
  selectedCell: {
    borderWidth: 2,
    borderColor: "#C4453D", // Red border
  },
  hasBookingsCell: {
    backgroundColor: "#FDF3E3",
  },
  deliveryDayCell: {
    // No border - borders only on today
  },
  dayText: {
    fontSize: 10, // Original 12, reduced by 2pts
    color: "#2E3A38",
    fontWeight: "500",
  },
  todayText: {
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  selectedText: {
    color: "#2E3A38", // Dark color so it's visible
    fontWeight: "bold",
  },
  bookingDot: {
    marginTop: 2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#1F8A5B",
    alignItems: "center",
    justifyContent: "center",
  },
  bookingCount: {
    fontSize: 9,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  productsList: {
    marginTop: 1,
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  productItemContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 0.5,
    width: "100%",
  },
  productBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#0E7C6B",
    marginRight: 3,
    flexShrink: 0,
  },
  productItem: {
    fontSize: 11, // Increased by 1 more from 10
    color: "#2E3A38",
    fontWeight: "500",
    lineHeight: 13, // Increased proportionally
    flexShrink: 1, // Allow text to shrink and wrap
  },
  selectedDateSection: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 12,
    marginTop: 12,
    marginBottom: 16,
    borderRadius: 8,
    padding: 14,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  selectedDateTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  noBookingsText: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
    paddingVertical: 16,
  },
  bookingsList: {
    gap: 10,
  },
  bookingCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  rentalCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 10,
  },
  rentalBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  deliveryBadge: {
    backgroundColor: "#1F8A5B",
  },
  pickupBadge: {
    backgroundColor: "#B4761B",
  },
  rentalBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  rentalInfo: {
    padding: 10,
  },
  namePhoneRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },
  durationMilesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },
  customerName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  rentalDetails: {
    fontSize: 12,
    color: "#5C6B68",
    marginBottom: 3,
  },
  product1Item: {
    color: "#0E7C6B", // Blue
  },
  product2Item: {
    color: "#1F8A5B", // Green
  },
  productsContainer: {
    alignItems: "center",
    marginTop: 3,
  },
  deliveryPickupCounts: {
    flexDirection: "row",
    gap: 4,
    marginTop: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  deliveryCountText: {
    fontSize: 11,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  pickupCountText: {
    fontSize: 11,
    color: "#1F8A5B",
    fontWeight: "bold",
  },
  topButtonsContainer: {
    flexDirection: "row",
    marginTop: 10,
    marginBottom: 10,
    marginHorizontal: 12,
    gap: 10,
  },
  deliveryPlannerButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    opacity: 1,
  },
  deliveryPlannerButtonDisabled: {
    opacity: 0.5,
    backgroundColor: "#8A9794",
  },
  deliveryPlannerButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  testPrintButton: {
    flex: 1,
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    opacity: 1,
  },
  testPrintButtonDisabled: {
    opacity: 0.5,
    backgroundColor: "#8A9794",
  },
  testPrintButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  tabContainer: {
    flexDirection: "row",
    backgroundColor: "#B4761B",
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginTop: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    alignItems: "center",
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    borderRadius: 6,
    marginHorizontal: 3,
  },
  tabActive: {
    backgroundColor: "#E3F2EF",
  },
  tabIcon: {
    fontSize: 14,
    marginRight: 5,
  },
  tabText: {
    fontSize: 12,
    color: "#5C6B68",
    fontWeight: "500",
  },
  tabTextActive: {
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  printButton: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "#0E7C6B",
    marginLeft: 6,
  },
  printButtonDisabled: {
    backgroundColor: "#AEB8B6",
    opacity: 0.5,
  },
  printButtonIcon: {
    fontSize: 18,
    color: "#FFFFFF",
  },
  listViewContainer: {
    flex: 1,
  },
  listSection: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 12,
    marginTop: 8,
    marginBottom: 16,
    borderRadius: 8,
    padding: 14,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  listItem: {
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  listCustomerName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 5,
  },
  listEquipment: {
    fontSize: 12,
    color: "#5C6B68",
    marginLeft: 6,
    marginBottom: 2,
  },
  totalsSummary: {
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#E3F2EF",
  },
  totalsSummaryTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 6,
  },
  totalItem: {
    fontSize: 12,
    color: "#2E3A38",
    marginBottom: 3,
    marginLeft: 6,
  },
});

export default Calendar;

